import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLocationContext } from '../../hooks/useLocationContext';
import {
  useLocalGetLocationByIdQuery,
  useGetIncidentFieldsQuery,
  useUpdateIncidentFieldsMutation,
} from '../../store/api/localApi';
import { IncidentCustomFieldDef, IncidentCustomFieldType } from '../../types';
import { useToast } from '../../components/Toast';

const MAX_FIELDS = 20;

const TYPE_LABELS: Record<IncidentCustomFieldType, string> = {
  [IncidentCustomFieldType.TEXT]: 'Short text',
  [IncidentCustomFieldType.TEXTAREA]: 'Long text',
  [IncidentCustomFieldType.SELECT]: 'Dropdown (single choice)',
  [IncidentCustomFieldType.MULTI_SELECT]: 'Multi-select',
  [IncidentCustomFieldType.CHECKBOX]: 'Checkbox (yes/no)',
  [IncidentCustomFieldType.RADIO]: 'Radio buttons',
  [IncidentCustomFieldType.NUMBER]: 'Number',
  [IncidentCustomFieldType.DATE]: 'Date',
};

const OPTION_TYPES = new Set<IncidentCustomFieldType>([
  IncidentCustomFieldType.SELECT,
  IncidentCustomFieldType.MULTI_SELECT,
  IncidentCustomFieldType.RADIO,
]);

/** Draft shape while editing — `options` kept as a raw comma-separated
 *  string so the input doesn't fight the user mid-keystroke; split/trimmed
 *  only at save time. */
interface DraftField extends Omit<IncidentCustomFieldDef, 'options'> {
  optionsText: string;
}

let draftKeySeq = 0;
function newDraftKey() {
  draftKeySeq += 1;
  return `new_field_${draftKeySeq}`;
}

function toDraft(f: IncidentCustomFieldDef): DraftField {
  return { ...f, optionsText: (f.options ?? []).join(', ') };
}

function fromDraft(d: DraftField): IncidentCustomFieldDef {
  const options = d.optionsText
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    key: d.key,
    type: d.type,
    label: d.label,
    required: d.required,
    order: d.order,
    ...(OPTION_TYPES.has(d.type) ? { options } : {}),
  };
}

/**
 * Org-scoped admin surface for per-org custom incident fields (Task 24).
 *
 * ORG-ADMIN ONLY (ORG_ADMIN or SUPER_ADMIN) — matches the backend's
 * `org:manage` gate on the PUT handler (see update-incident-fields/index.ts's
 * doc comment for why LOCATION_ADMIN is deliberately excluded even though
 * `incident:manage-fields` alone would let one through). This component
 * assumes it is only mounted for an org-admin-level caller; the backend
 * still re-enforces it regardless of what this page renders.
 *
 * The whole list is a SINGLE PUT (replace semantics) — there is no per-field
 * create/update endpoint. Local edits accumulate in `drafts` and are only
 * sent on "Save changes".
 */
const IncidentFieldsAdmin: React.FC = () => {
  const { isOrgLevel, activeOrganizationId } = useAuth();
  const { effectiveLocationId } = useLocationContext();
  const { showSuccess, showError } = useToast();

  const { data: location } = useLocalGetLocationByIdQuery(effectiveLocationId as string, {
    skip: !!activeOrganizationId || !effectiveLocationId,
  });
  const organizationId = activeOrganizationId || location?.organizationId;

  const { data, isLoading, isError } = useGetIncidentFieldsQuery(
    { organizationId: organizationId as string },
    { skip: !organizationId }
  );
  const [updateFields, { isLoading: isSaving }] = useUpdateIncidentFieldsMutation();

  const [drafts, setDrafts] = useState<DraftField[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.config) {
      setDrafts(data.config.fields.map(toDraft));
      setDirty(false);
    }
  }, [data]);

  if (!isOrgLevel) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500" data-testid="incident-fields-admin-no-access">
          You don't have access to manage incident fields.
        </p>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500">Resolving organisation…</p>
      </div>
    );
  }

  const updateDraft = (index: number, patch: Partial<DraftField>) => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    setDirty(true);
  };

  const handleAdd = () => {
    if (drafts.length >= MAX_FIELDS) return;
    setDrafts((prev) => [
      ...prev,
      {
        key: newDraftKey(),
        type: IncidentCustomFieldType.TEXT,
        label: '',
        required: false,
        order: prev.length,
        optionsText: '',
      },
    ]);
    setDirty(true);
  };

  const handleRemove = (index: number) => {
    setDrafts((prev) => prev.filter((_, i) => i !== index).map((d, i) => ({ ...d, order: i })));
    setDirty(true);
  };

  const handleMove = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= drafts.length) return;
    setDrafts((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((d, i) => ({ ...d, order: i }));
    });
    setDirty(true);
  };

  const keys = drafts.map((d) => d.key.trim());
  const duplicateKeys = new Set(keys.filter((k, i) => k && keys.indexOf(k) !== i));
  const keyPattern = /^[a-z0-9_]+$/;
  const invalidKeyIndexes = drafts
    .map((d, i) => (d.key && !keyPattern.test(d.key) ? i : -1))
    .filter((i) => i >= 0);
  const missingLabelIndexes = drafts
    .map((d, i) => (!d.label.trim() ? i : -1))
    .filter((i) => i >= 0);
  const missingOptionsIndexes = drafts
    .map((d, i) => (OPTION_TYPES.has(d.type) && !d.optionsText.trim() ? i : -1))
    .filter((i) => i >= 0);

  const canSave =
    dirty &&
    !isSaving &&
    drafts.length <= MAX_FIELDS &&
    duplicateKeys.size === 0 &&
    invalidKeyIndexes.length === 0 &&
    missingLabelIndexes.length === 0 &&
    missingOptionsIndexes.length === 0 &&
    drafts.every((d) => d.key.trim().length > 0);

  const handleSave = async () => {
    if (!canSave || !organizationId) return;
    try {
      await updateFields({
        organizationId,
        fields: drafts.map(fromDraft),
      }).unwrap();
      showSuccess('Incident fields saved.');
      setDirty(false);
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string } | undefined }).data
          : undefined;
      showError(data?.error || 'Failed to save incident fields.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <section data-testid="incident-fields-admin" className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Incident report — custom fields</h2>
          <p className="mt-1 text-sm text-gray-500">
            Extra fields your organisation wants captured on every incident report, in addition to
            the standard fields (severity, description, witness, etc.). These are stored and printed
            only — they don't affect Tusla deadlines or workflow. Up to {MAX_FIELDS} fields.
          </p>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-rose-600">Failed to load the current configuration.</p>
        ) : (
          <>
            <div className="space-y-4">
              {drafts.length === 0 && (
                <p className="text-sm text-gray-500" data-testid="incident-fields-empty">
                  No custom fields configured yet.
                </p>
              )}
              {drafts.map((d, i) => (
                <div
                  key={d.key || i}
                  data-testid={`incident-field-row-${i}`}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3"
                >
                  <div className="flex flex-wrap gap-3 items-start">
                    <div className="flex-1 min-w-[160px]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Key</label>
                      <input
                        type="text"
                        data-testid={`incident-field-key-${i}`}
                        value={d.key}
                        onChange={(e) =>
                          updateDraft(i, { key: e.target.value.trim().toLowerCase() })
                        }
                        placeholder="e.g. yard_zone"
                        className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {duplicateKeys.has(d.key.trim()) && (
                        <p
                          className="mt-1 text-xs text-red-600"
                          data-testid={`incident-field-key-error-${i}`}
                        >
                          Duplicate key.
                        </p>
                      )}
                      {invalidKeyIndexes.includes(i) && (
                        <p className="mt-1 text-xs text-red-600">
                          Lowercase letters, numbers, underscores only.
                        </p>
                      )}
                    </div>

                    <div className="flex-1 min-w-[160px]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        data-testid={`incident-field-label-${i}`}
                        value={d.label}
                        onChange={(e) => updateDraft(i, { label: e.target.value })}
                        placeholder="e.g. Yard zone"
                        className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {missingLabelIndexes.includes(i) && (
                        <p className="mt-1 text-xs text-red-600">Label is required.</p>
                      )}
                    </div>

                    <div className="min-w-[180px]">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                      <select
                        data-testid={`incident-field-type-${i}`}
                        value={d.type}
                        onChange={(e) =>
                          updateDraft(i, { type: e.target.value as IncidentCustomFieldType })
                        }
                        className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {Object.values(IncidentCustomFieldType).map((t) => (
                          <option key={t} value={t}>
                            {TYPE_LABELS[t]}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end pb-1.5">
                      <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          data-testid={`incident-field-required-${i}`}
                          checked={d.required}
                          onChange={(e) => updateDraft(i, { required: e.target.checked })}
                        />
                        Required
                      </label>
                    </div>
                  </div>

                  {OPTION_TYPES.has(d.type) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Options (comma-separated)
                      </label>
                      <input
                        type="text"
                        data-testid={`incident-field-options-${i}`}
                        value={d.optionsText}
                        onChange={(e) => updateDraft(i, { optionsText: e.target.value })}
                        placeholder="e.g. Yard, Classroom, Hallway"
                        className="block w-full text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {missingOptionsIndexes.includes(i) && (
                        <p className="mt-1 text-xs text-red-600">
                          At least one option is required for this field type.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      data-testid={`incident-field-move-up-${i}`}
                      onClick={() => handleMove(i, -1)}
                      disabled={i === 0}
                      className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      data-testid={`incident-field-move-down-${i}`}
                      onClick={() => handleMove(i, 1)}
                      disabled={i === drafts.length - 1}
                      className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Move down
                    </button>
                    <button
                      type="button"
                      data-testid={`incident-field-remove-${i}`}
                      onClick={() => handleRemove(i)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                data-testid="incident-field-add"
                onClick={handleAdd}
                disabled={drafts.length >= MAX_FIELDS}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                + Add field
              </button>
              <p className="text-xs text-gray-400">
                {drafts.length} / {MAX_FIELDS}
              </p>
            </div>

            <div className="flex justify-end border-t border-gray-100 pt-4">
              <button
                type="button"
                data-testid="incident-fields-save"
                onClick={handleSave}
                disabled={!canSave}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {isSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default IncidentFieldsAdmin;
